/**
 * HelpContentContext
 *
 * Phase 8 Prompt 2 — Help Content System Foundation.
 * Per audit deliverable 04B_HELP_GLOSSARY_AND_TOOLTIPS.md Section J.
 *
 * Loads the help-content.json registry at app boot and exposes O(1) lookups
 * via React Context. Convenience hooks below for the most common patterns.
 *
 * If a referenced id is missing, dev mode logs a console warning; production
 * silently returns null (never blocks the UI).
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from "react";
import helpContentData from "../data/help-content.json";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GlossaryEntry {
  term: string;
  definition: string;
  whyThisMatters?: string;
  related?: string[];
  where?: string[];
  formula?: string;
}

export interface TooltipEntry {
  surface: string;
  element: string;
  copy: string;
}

export interface FieldHelperEntry {
  surface: string;
  input: string;
  copy: string;
}

export interface EmptyStateEntry {
  icon?: string;
  whatIsThis: string;
  whyUse: string;
  ctaLabel: string | null;
}

export interface ActionConfirmEntry {
  label: string;
  copy: string;
  confirmType:
    | "one-click"
    | "type-to-confirm"
    | "reason-required"
    | "blocked-with-suggestion";
  confirmText?: string;
  destructive: boolean;
}

export interface StateBadgeEntry {
  label: string;
  color: string;
  icon: string;
  semantic: string;
  transitionOut: string;
}

export interface KpiEntry {
  label: string;
  formula: string;
  definition: string;
  whyThisMatters: string;
  benchmarkLow: number | null;
  benchmarkHigh: number | null;
  unit?: string;
  source: string;
}

export interface CoachmarkStep {
  step: number;
  target: string;
  copy: string;
}

interface HelpContent {
  version: string;
  lastUpdated: string;
  source: string;
  glossary: GlossaryEntry[];
  tooltips: Record<string, TooltipEntry>;
  fieldHelpers: Record<string, FieldHelperEntry>;
  emptyStates: Record<string, EmptyStateEntry>;
  actionConfirms: Record<string, ActionConfirmEntry>;
  stateBadges: Record<string, StateBadgeEntry>;
  kpis: Record<string, KpiEntry>;
  coachmarks: Record<string, CoachmarkStep[]>;
  commonTasks: Record<string, string[]>;
}

// ---------------------------------------------------------------------------
// Drawer-state shared via context (avoids prop-drilling open/close from TopBar)
// ---------------------------------------------------------------------------

interface HelpDrawerState {
  open: boolean;
  initialTab?: string;
  surfaceContext?: string;
}

interface HelpContextValue {
  content: HelpContent;
  drawer: HelpDrawerState;
  openDrawer: (opts?: { initialTab?: string; surfaceContext?: string }) => void;
  closeDrawer: () => void;

  // Lookup helpers (O(1) where keyed; O(n) for glossary find-by-term)
  getTooltip: (id: string) => TooltipEntry | null;
  getFieldHelper: (id: string) => FieldHelperEntry | null;
  getEmptyState: (id: string) => EmptyStateEntry | null;
  getActionConfirm: (id: string) => ActionConfirmEntry | null;
  getStateBadge: (state: string) => StateBadgeEntry | null;
  getKpi: (id: string) => KpiEntry | null;
  getCoachmarks: (id: string) => CoachmarkStep[] | null;
  getCommonTasks: (surfaceId: string) => string[];
  findGlossaryEntry: (term: string) => GlossaryEntry | null;
  searchGlossary: (query: string) => GlossaryEntry[];
}

const HelpContentContext = createContext<HelpContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface HelpContentProviderProps {
  children: ReactNode;
}

export const HelpContentProvider: React.FC<HelpContentProviderProps> = ({
  children,
}) => {
  // Treat the imported JSON as the canonical content; no async fetch in Prompt 2.
  // A later prompt may switch to an API-served version; this contract doesn't
  // need to change.
  const content = helpContentData as unknown as HelpContent;

  const [drawer, setDrawer] = useState<HelpDrawerState>({ open: false });

  const openDrawer = useCallback(
    (opts?: { initialTab?: string; surfaceContext?: string }) => {
      setDrawer({
        open: true,
        initialTab: opts?.initialTab,
        surfaceContext: opts?.surfaceContext,
      });
    },
    [],
  );

  const closeDrawer = useCallback(() => {
    setDrawer((prev) => ({ ...prev, open: false }));
  }, []);

  // Lookup helpers with dev-mode warnings
  const value: HelpContextValue = useMemo(() => {
    const warnMissing = (kind: string, id: string) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[HelpContent] Missing ${kind} entry for id="${id}"`);
      }
    };

    return {
      content,
      drawer,
      openDrawer,
      closeDrawer,

      getTooltip: (id: string) => {
        const entry = content.tooltips[id];
        if (!entry) warnMissing("tooltip", id);
        return entry ?? null;
      },
      getFieldHelper: (id: string) => {
        const entry = content.fieldHelpers[id];
        if (!entry) warnMissing("fieldHelper", id);
        return entry ?? null;
      },
      getEmptyState: (id: string) => {
        const entry = content.emptyStates[id];
        if (!entry) warnMissing("emptyState", id);
        return entry ?? null;
      },
      getActionConfirm: (id: string) => {
        const entry = content.actionConfirms[id];
        if (!entry) warnMissing("actionConfirm", id);
        return entry ?? null;
      },
      getStateBadge: (state: string) => {
        const entry = content.stateBadges[state];
        if (!entry) warnMissing("stateBadge", state);
        return entry ?? null;
      },
      getKpi: (id: string) => {
        const entry = content.kpis[id];
        if (!entry) warnMissing("kpi", id);
        return entry ?? null;
      },
      getCoachmarks: (id: string) => {
        const entry = content.coachmarks[id];
        if (!entry) warnMissing("coachmarks", id);
        return entry ?? null;
      },
      getCommonTasks: (surfaceId: string) => {
        return content.commonTasks[surfaceId] ?? [];
      },
      findGlossaryEntry: (term: string) => {
        const normalized = term.toLowerCase().trim();
        return (
          content.glossary.find(
            (g) => g.term.toLowerCase() === normalized,
          ) ?? null
        );
      },
      searchGlossary: (query: string) => {
        const q = query.toLowerCase().trim();
        if (!q) return content.glossary;
        return content.glossary.filter(
          (g) =>
            g.term.toLowerCase().includes(q) ||
            g.definition.toLowerCase().includes(q),
        );
      },
    };
  }, [content, drawer, openDrawer, closeDrawer]);

  return (
    <HelpContentContext.Provider value={value}>
      {children}
    </HelpContentContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useHelpContent(): HelpContextValue {
  const ctx = useContext(HelpContentContext);
  if (!ctx) {
    throw new Error(
      "useHelpContent must be used inside <HelpContentProvider>. " +
        "Wrap your app root in App.tsx.",
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Utility: interpolate {{variable}} placeholders in copy strings
// Used by ActionConfirm to render context-bound consequence previews.
// ---------------------------------------------------------------------------

export function interpolateCopy(
  template: string,
  context: Record<string, string | number | undefined>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = context[key];
    if (value === undefined || value === null) {
      return `{{${key}}}`; // leave placeholder visible if not provided
    }
    return String(value);
  });
}
